import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Tooltip,
  TextField,
  InputAdornment,
  TablePagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { 
  Add, 
  Visibility as VisibilityIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  History as HistoryIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  GetApp as GetAppIcon,
} from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import apiClient from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

type WorkflowStatus = 'DRAFT' | 'PENDING_MANAGER_APPROVAL' | 'PENDING_LEGAL' | 'PENDING_AP' | 'COMPLETED' | 'REJECTED';

interface VendorRequest {
  id: string;
  companyName: string;
  requestorName: string;
  requestorEmail: string;
  requestorManager: string;
  requestDate: string;
  status: WorkflowStatus;
  estimatedAmount: number;
  active: boolean;
  rejectionReason?: string;
  vendorAddedToRamp?: boolean;
  lastEditedByUserId?: string;
  lastEditedByEmail?: string;
  lastEditedByName?: string;
  lastEditedAt?: string;
}

const VendorDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [requests, setRequests] = useState<VendorRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);
  const [auditData, setAuditData] = useState<any[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [showRemoved, setShowRemoved] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [statusFilter, setStatusFilter] = useState<WorkflowStatus | 'ALL'>('ALL');
  const [currentAuditCompanyName, setCurrentAuditCompanyName] = useState<string>('');

  useEffect(() => {
    loadRequests();
  }, [showRemoved]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter requests based on search query and status
  const filteredRequests = requests.filter(request => {
    const matchesSearch = request.companyName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'ALL' || request.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Paginate filtered results
  const paginatedRequests = filteredRequests.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const loadRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get('/api/vendors', {
        params: { includeInactive: showRemoved }
      });
      const submissions = response.data.data || [];
      
      // Map database fields to dashboard format
      const mapped = submissions.map((sub: any) => ({
        id: String(sub.id),
        companyName: sub.company_name || 'N/A',
        requestorName: sub.requestor_name || 'Unknown',
        requestorEmail: sub.requestor_email || '',
        requestorManager: sub.requestor_manager || 'N/A',
        requestDate: sub.request_date || sub.created_at,
        status: sub.workflow_status as WorkflowStatus,
        estimatedAmount: parseFloat(sub.estimated_contract_amount) || 0,
        active: sub.active !== false,
        rejectionReason: sub.rejection_reason || undefined,
        vendorAddedToRamp: sub.vendor_added_to_ramp || false,
        lastEditedByUserId: sub.last_edited_by_user_id || undefined,
        lastEditedByEmail: sub.last_edited_by_email || undefined,
        lastEditedByName: sub.last_edited_by_name || undefined,
        lastEditedAt: sub.last_edited_at || undefined,
      }));
      
      // Sort by request date descending (newest first)
      mapped.sort((a: VendorRequest, b: VendorRequest) => {
        const dateA = new Date(a.requestDate).getTime();
        const dateB = new Date(b.requestDate).getTime();
        return dateB - dateA;
      });
      
      setRequests(mapped);
    } catch (error: any) {
      console.error('=== DASHBOARD ERROR DETAILS ===');
      console.error('Error loading requests:', error);
      console.error('Error response:', error.response);
      console.error('Error data:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error code:', error.response?.data?.code);
      console.error('Error details:', error.response?.data?.details);
      console.error('Full error object:', JSON.stringify(error.response?.data, null, 2));
      console.error('================================');
      
      if (error.response?.status === 401) {
        setError('Authentication required. Please log in.');
      } else {
        const backendError = error.response?.data?.error || error.response?.data?.details || 'Failed to load vendor requests.';
        setError(`Failed to load vendor requests: ${backendError}`);
      }
      setRequests([]); // Set empty array to stop any retry attempts
    } finally {
      setLoading(false);
    }
  };

  const getStatusChip = (status: WorkflowStatus) => {
    const statusConfig: Record<WorkflowStatus, { label: string; color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' }> = {
      DRAFT: { label: 'Draft', color: 'default' },
      PENDING_MANAGER_APPROVAL: { label: 'Pending Manager Approval', color: 'warning' },
      PENDING_LEGAL: { label: 'Pending Legal', color: 'info' },
      PENDING_AP: { label: 'Pending AP', color: 'primary' },
      COMPLETED: { label: 'Completed', color: 'success' },
      REJECTED: { label: 'Rejected', color: 'error' },
    };
    const config = statusConfig[status];
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleViewEdit = (id: string) => {
    navigate(`/form/${id}`);
  };

  const handleCreateNew = () => {
    navigate('/form/new');
  };

  const handleDeleteClick = (id: string, companyName: string) => {
    setDeleteTarget({ id, name: companyName });
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    
    try {
      await apiClient.delete(`/api/vendors/${deleteTarget.id}`);
      setRequests(requests.filter(r => r.id !== deleteTarget.id));
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    } catch (error) {
      console.error('Error deleting submission:', error);
      setError('Failed to delete submission');
    }
  };

  const handleAuditClick = async (id: string, companyName: string) => {
    setAuditLoading(true);
    setAuditDialogOpen(true);
    setCurrentAuditCompanyName(companyName);
    
    try {
      const response = await apiClient.get(`/api/vendors/${id}/audit`);
      setAuditData(response.data.data || []);
    } catch (error) {
      console.error('Error loading audit trail:', error);
      setAuditData([]);
    } finally {
      setAuditLoading(false);
    }
  };

  const handleExportTable = () => {
    // Convert filtered requests to CSV
    const headers = ['ID', 'Company Name', 'Requestor', 'Manager', 'Request Date', 'Status', 'Estimated Amount', 'Vendor in Ramp'];
    const csvRows = [headers.join(',')];
    
    filteredRequests.forEach(req => {
      const row = [
        req.id,
        `"${req.companyName}"`,
        `"${req.requestorName}"`,
        `"${req.requestorManager}"`,
        `"${formatDate(req.requestDate)}"`, // Quote the date to prevent splitting
        req.status,
        req.estimatedAmount,
        req.vendorAddedToRamp ? 'TRUE' : 'FALSE'
      ];
      csvRows.push(row.join(','));
    });
    
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `vendor_dashboard_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportAuditHistory = () => {
    if (auditData.length === 0) return;
    
    const headers = ['Company Name', 'Date/Time', 'Action', 'User', 'Stage', 'Comment'];
    const csvRows = [headers.join(',')];
    
    auditData.forEach(entry => {
      const row = [
        `"${currentAuditCompanyName}"`,
        new Date(entry.created_at).toLocaleString(),
        entry.action_type || '',
        `"${entry.user_name || entry.user_id || 'Unknown'}"`,
        entry.workflow_stage || '',
        `"${(entry.comment || '').replace(/"/g, '""')}"`
      ];
      csvRows.push(row.join(','));
    });
    
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `audit_history_${currentAuditCompanyName.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Contractor / Vendor Due Diligence Requests
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          size="large"
          onClick={handleCreateNew}
        >
          Create New Request
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search by Company Name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: '300px' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Status Filter</InputLabel>
          <Select
            value={statusFilter}
            label="Status Filter"
            onChange={(e) => {
              setStatusFilter(e.target.value as WorkflowStatus | 'ALL');
              setPage(0);
            }}
          >
            <MenuItem value="ALL">All Statuses</MenuItem>
            <MenuItem value="DRAFT">Draft</MenuItem>
            <MenuItem value="PENDING_MANAGER_APPROVAL">Pending Manager</MenuItem>
            <MenuItem value="PENDING_LEGAL">Pending Legal</MenuItem>
            <MenuItem value="PENDING_AP">Pending AP</MenuItem>
            <MenuItem value="COMPLETED">Completed</MenuItem>
            <MenuItem value="REJECTED">Rejected</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleExportTable}
          disabled={filteredRequests.length === 0}
        >
          Export Table ({filteredRequests.length})
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        {isAdmin && (
          <FormControlLabel
            control={
              <Switch
                checked={showRemoved}
                onChange={(e) => setShowRemoved(e.target.checked)}
                color="primary"
              />
            }
            label="Show removed items"
          />
        )}
      </Box>

      <TableContainer component={Paper} elevation={2}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong>ID</strong></TableCell>
              <TableCell><strong>Company Name</strong></TableCell>
              <TableCell>Requestor</TableCell>
              <TableCell>Manager</TableCell>
              <TableCell>Request Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Estimated Amount</TableCell>
              <TableCell align="right">Actions</TableCell>
              {isAdmin && <TableCell align="right">Admin</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={isAdmin ? 10 : 9} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredRequests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isAdmin ? 10 : 9} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    {searchQuery ? 'No requests match your search.' : 'No requests found. Click "Create New Request" to get started.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedRequests.map((request) => (
                <TableRow
                  key={request.id}
                  sx={{ '&:hover': { backgroundColor: '#fafafa' } }}
                >
                  <TableCell>{request.id}</TableCell>
                  <TableCell>{request.companyName}</TableCell>
                  <TableCell>{request.requestorName}</TableCell>
                  <TableCell>{request.requestorManager}</TableCell>
                  <TableCell>{formatDate(request.requestDate)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getStatusChip(request.status)}
                      {request.status === 'REJECTED' && request.rejectionReason && (
                        <Tooltip title={request.rejectionReason} arrow placement="top">
                          <InfoOutlinedIcon sx={{ color: 'error.main', fontSize: '1.2rem' }} />
                        </Tooltip>
                      )}
                      {/* Show blue info icon if admin edited but requestor hasn't submitted yet */}
                      {(request.status === 'DRAFT' || request.status === 'REJECTED') && 
                       request.lastEditedByEmail && 
                       request.requestorEmail && 
                       request.lastEditedByEmail.toLowerCase() !== request.requestorEmail.toLowerCase() && (
                        <Tooltip 
                          title={`Edits were made by ${request.lastEditedByName || request.lastEditedByEmail}, ${request.requestorName} still needs to submit.`} 
                          arrow 
                          placement="top"
                        >
                          <InfoOutlinedIcon sx={{ color: 'info.main', fontSize: '1.2rem' }} />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">{formatCurrency(request.estimatedAmount)}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleViewEdit(request.id)}
                      title={request.status === 'DRAFT' || request.status === 'REJECTED' ? 'Edit' : 'View'}
                    >
                      {request.status === 'DRAFT' || request.status === 'REJECTED' ? (
                        <EditIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>
                  </TableCell>
                  {isAdmin && (
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleAuditClick(request.id, request.companyName)}
                        title="View Audit Trail"
                      >
                        <HistoryIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => {
                          window.open(`${process.env.REACT_APP_API_URL}/api/vendors/${request.id}/export`, '_blank');
                        }}
                        title="Download Excruciating Detail"
                      >
                        <GetAppIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(request.id, request.companyName)}
                        title="Delete"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={filteredRequests.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove <strong>{deleteTarget?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>No</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Yes, Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Audit Trail Dialog */}
      <Dialog 
        open={auditDialogOpen} 
        onClose={() => setAuditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Audit Trail - {currentAuditCompanyName}</span>
            <Button
              startIcon={<DownloadIcon />}
              onClick={handleExportAuditHistory}
              disabled={auditData.length === 0}
              size="small"
            >
              Export
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {auditLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : auditData.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date/Time</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Stage</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Notes/Rejection Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {auditData.map((entry, index) => (
                    <TableRow key={index}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {new Date(entry.created_at).toLocaleString('en-US', {
                          year: '2-digit',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true
                        })}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={entry.action_type} 
                          size="small"
                          // Surface anything that failed, not just rejections: Ironclad
                          // launch/owner-assignment failures are recorded as *_FAILED and
                          // would otherwise look identical to a successful step.
                          color={
                            entry.action_type === 'REJECTED' || entry.action_type?.endsWith('_FAILED')
                              ? 'error'
                              : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell>{entry.workflow_stage}</TableCell>
                      <TableCell sx={{ fontSize: '0.85rem' }}>
                        {entry.user_name || entry.user_email || entry.user_id}
                        {entry.user_email && entry.user_name && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {entry.user_email}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{entry.rejection_reason || entry.notes || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography>No audit trail data available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAuditDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VendorDashboard;
